import { call, put } from 'redux-saga/effects';
import { get } from 'ravenjs/utils/lodash';

import {
    deleteBannerAPI,
    dismissBannerAPI,
    fetchBanners,
    updateBannerById,
    createBannerAPI,
    fetchBannersV3,
    createBannerV3,
    updateBannerV3,
    deleteBannerV3,
    dismissBannerV3,
} from 'modules/apis';
import {
    getUpdateBannerPayload,
    getCreateBannerPayload,
    getDismissBannerPayload,
} from 'utils/banners';

import * as adminActions from '../actions';

/**
 * Fetch banner's list
 *
 * @method fetchBannersSaga
 * @type   {Generator}
 * @param  {Object}     action The redux action
 * @return {Object}     banners
 */
export function* fetchBannersSaga(action) {
    try {
        const { payload: params } = action;
        const resp = yield call(fetchBanners, params);
        const banners = get(resp, 'data', {});
        yield put(adminActions.fetchBanners.success(banners));
        return banners;
    } catch (error) {
        yield put(adminActions.fetchBanners.error(error));
        return error;
    }
}

/**
 * Update banner by id.
 *
 * @method updateBannerSaga
 * @type   {Generator}
 * @param  {Object}     action The redux action
 * @return {Object}     banner
 */
export function* updateBannerSaga(action) {
    try {
        const { payload: params } = action;
        const updatedBanner = getUpdateBannerPayload(params);
        const resp = yield call(updateBannerById, updatedBanner);
        const banner = get(resp, 'data', {});
        yield put(adminActions.updateBanner.success(banner));
        return banner;
    } catch (error) {
        yield put(adminActions.updateBanner.error(error));
        return error;
    }
}

/**
 * Create banner.
 *
 * @method createBannerSaga
 * @type   {Generator}
 * @param  {Object}     action The redux action
 * @return {Object}     banner
 */
export function* createBannerSaga(action) {
    try {
        const { payload: params } = action;
        const createBanner = getCreateBannerPayload(params);
        const resp = yield call(createBannerAPI, createBanner);
        const banner = get(resp, 'data', {});
        yield put(adminActions.createBanner.success(banner));
        return banner;
    } catch (error) {
        yield put(adminActions.createBanner.error(error));
        return error;
    }
}

/**
 * Delete banner.
 *
 * @method deleteBannerSaga
 * @type   {Generator}
 * @param  {Object}     action The redux action
 * @return {Object}     banner
 */
export function* deleteBannerSaga(action) {
    try {
        const { payload: params } = action;
        const { bannerId } = params;
        const resp = yield call(deleteBannerAPI, bannerId);
        const data = get(resp, 'data', {});
        yield put(adminActions.deleteBanner.success(data));
        return data;
    } catch (error) {
        yield put(adminActions.deleteBanner.error(error));
        return error;
    }
}

/**
 * Dismiss banner.
 *
 * @method dismissBannerSaga
 * @type   {Generator}
 * @param  {Object}     action The redux action
 * @return {Object}     banner
 */
export function* dismissBannerSaga(action) {
    try {
        const { payload: params } = action;
        const payload = getDismissBannerPayload(params);
        const resp = yield call(dismissBannerAPI, payload);
        const data = get(resp, 'data', {});
        yield put(adminActions.dismissBanner.success(data));
        return data;
    } catch (error) {
        yield put(adminActions.dismissBanner.error(error));
        return error;
    }
}

/**
 * Fetch banner's list (v3).
 *
 * The v3 response nests the array under `banners` alongside `totalRecords`,
 * `limit`, `offset` and `sort`; the full data object is returned so callers
 * can drive server-side pagination.
 *
 * @method fetchBannersV3Saga
 * @type   {Generator}
 * @param  {Object}     action The redux action
 * @return {Object}     banners response
 */
export function* fetchBannersV3Saga(action) {
    try {
        const { payload: params } = action;
        const resp = yield call(fetchBannersV3, params);
        const banners = get(resp, 'data', {});
        yield put(adminActions.fetchBannersV3.success(banners));
        return banners;
    } catch (error) {
        yield put(adminActions.fetchBannersV3.error(error));
        return error;
    }
}

/**
 * Create banner (v3).
 *
 * @method createBannerV3Saga
 * @type   {Generator}
 * @param  {Object}     action The redux action
 * @return {Object}     banner
 */
export function* createBannerV3Saga(action) {
    try {
        const { payload: params } = action;
        const resp = yield call(createBannerV3, params);
        const banner = get(resp, 'data', {});
        yield put(adminActions.createBannerV3.success(banner));
        return banner;
    } catch (error) {
        yield put(adminActions.createBannerV3.error(error));
        return error;
    }
}

/**
 * Update banner (v3).
 *
 * @method updateBannerV3Saga
 * @type   {Generator}
 * @param  {Object}     action The redux action
 * @return {Object}     banner
 */
export function* updateBannerV3Saga(action) {
    try {
        const { payload: params } = action;
        const resp = yield call(updateBannerV3, params);
        const banner = get(resp, 'data', {});
        yield put(adminActions.updateBannerV3.success(banner));
        return banner;
    } catch (error) {
        yield put(adminActions.updateBannerV3.error(error));
        return error;
    }
}

/**
 * Delete banner (v3).
 *
 * @method deleteBannerV3Saga
 * @type   {Generator}
 * @param  {Object}     action The redux action
 * @return {Object}     banner
 */
export function* deleteBannerV3Saga(action) {
    try {
        const { payload: params } = action;
        const { bannerId } = params;
        const resp = yield call(deleteBannerV3, bannerId);
        const data = get(resp, 'data', {});
        yield put(adminActions.deleteBannerV3.success(data));
        return data;
    } catch (error) {
        yield put(adminActions.deleteBannerV3.error(error));
        return error;
    }
}

/**
 * Dismiss banner (v3). No request body is sent.
 *
 * @method dismissBannerV3Saga
 * @type   {Generator}
 * @param  {Object}     action The redux action
 * @return {Object}     banner
 */
export function* dismissBannerV3Saga(action) {
    try {
        const { payload: params } = action;
        const { bannerId } = params;
        const resp = yield call(dismissBannerV3, bannerId);
        const data = get(resp, 'data', {});
        yield put(adminActions.dismissBannerV3.success(data));
        return data;
    } catch (error) {
        yield put(adminActions.dismissBannerV3.error(error));
        return error;
    }
}
